import { CITY } from '@city'
import { StoreLinks } from '../../scripts/storeLinks.mjs'
import type { StoreSlot } from '../../scripts/storeLinks.mjs'
import { TrackingLinks } from '../../scripts/trackingLinks.mjs'
import type { StoreKind } from '../analytics/storeOutboundEvents'

/*
 * The platform the links service (repo `mmthstudio/links`,
 * `redirect/platform.go`) and the nginx fallback map `$go_platform` assign to
 * a click — bots first (Applebot announces an iPhone), then iOS, then Android.
 * Kept identical to both lists: the decision below is only right if the
 * server classifies the visitor the same way.
 */
const BOT =
  /(bot|crawler|spider|facebookexternalhit|facebookcatalog|preview|embedly|slurp|whatsapp|vkshare|skypeuripreview|bitlybot|discord|slack)/i
const IOS = /(iphone|ipad|ipod)/i
const ANDROID = /android/i

/** True when the links service would send this visitor to `store`. */
function serverSendsTo(userAgent: string, store: StoreKind): boolean {
  if (BOT.test(userAgent)) return false
  if (IOS.test(userAgent)) return store === 'app_store'
  return ANDROID.test(userAgent) && store === 'google_play'
}

/**
 * Href of a site "get the app" CTA for `store` (TASK-1170).
 *
 * When the visitor's phone is the one `store` serves, the CTA goes through
 * the city's own tracking link `https://<CITY.domain>/go/site-<surface>`:
 * the links service sends it on to the same store, opens the app when it is
 * installed, and counts the click with every other channel (`go-report`,
 * Amplitude `install_attributed`).
 *
 * Every other visitor keeps the direct, slot-tagged store URL (TASK-644). The
 * service sends a desktop — and iPadOS in desktop mode, which reports a Mac
 * user agent — back to the site's home, and an iPhone to the App Store even
 * from the Google Play badge, so `/go/` there would take the visitor
 * somewhere other than the store the button names.
 *
 * @param userAgent `navigator.userAgent`
 * @param store the store the CTA names
 * @param slot the surface being credited
 */
export function storeCtaHref(userAgent: string, store: StoreKind, slot: StoreSlot): string {
  if (serverSendsTo(userAgent, store)) {
    return TrackingLinks.url(TrackingLinks.siteSource(slot), CITY.domain)
  }
  return store === 'app_store' ? StoreLinks.appStore(slot) : StoreLinks.googlePlay(slot)
}
